import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { createSlug } from '../../../../util/urlHelpers';
import { ensureUser } from '../../../../util/data';
import { useLoading } from '../../../../context/loadingContext';
import css from './CustomFeatured.module.css';

const CustomFeatured = () => {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const touchStartTimeRef = useRef(0);
  const touchMovedRef = useRef(false);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const history = useHistory();
  const { setLoadingComplete } = useLoading();

  // Fetch featured listings
  useEffect(() => {
    const fetchFeaturedListings = async () => {
      setIsLoading(true);

      try {
        const apiBaseUrl = process.env.NODE_ENV === 'development'
          ? `http://localhost:${process.env.REACT_APP_DEV_API_SERVER_PORT}`
          : '';

        const response = await fetch(`${apiBaseUrl}/api/featured-listing/query`);

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();
        const listings = data.data || [];
        const included = data.included || [];

        // Build a lookup map for included resources
        const includedMap = {};
        included.forEach(resource => {
          const id = resource.id?.uuid || resource.id;
          includedMap[id] = resource;
        });

        // Resolve image and author references
        const resolvedListings = listings.map(listing => {
          const resolved = { ...listing };

          if (listing.relationships?.images?.data) {
            resolved.images = listing.relationships.images.data
              .map(imgRef => {
                const imgId = imgRef.id?.uuid || imgRef.id;
                return includedMap[imgId];
              })
              .filter(Boolean);
          }

          if (listing.relationships?.author?.data) {
            const authorId = listing.relationships.author.data.id?.uuid || listing.relationships.author.data.id;
            resolved.author = includedMap[authorId];
          }

          return resolved;
        });

        // Filter for published and non-deleted listings
        const validListings = resolvedListings.filter(listing => {
          const isPublished = listing?.attributes?.state === 'published';
          const notDeleted = !listing?.attributes?.deleted;
          return isPublished && notDeleted;
        }).slice(0, 8);

        setFeaturedListings(validListings);
      } catch (error) {
        console.error('Failed to fetch featured listings:', error);
        setFeaturedListings([]);
      } finally {
        setIsLoading(false);
        setTimeout(() => setLoadingComplete('homeFeatured'), 100);
      }
    };

    fetchFeaturedListings();
  }, [setLoadingComplete]);

  // Center listings if they don't fill container
  useEffect(() => {
    if (!carouselRef.current || isLoading || featuredListings.length === 0) return;

    const centerIfNeeded = () => {
      requestAnimationFrame(() => {
        const container = carouselRef.current;
        if (!container) return;

        const containerWidth = container.offsetWidth;
        const firstCard = container.children[0];

        if (!firstCard) return;

        const cardWidth = firstCard.offsetWidth;
        const gap = 16;
        const totalContentWidth = (cardWidth * featuredListings.length) + (gap * (featuredListings.length - 1));

        if (totalContentWidth < containerWidth) {
          const availableSpace = containerWidth - totalContentWidth;
          const leftPadding = availableSpace / 2;
          container.style.paddingLeft = `${leftPadding}px`;
        } else {
          container.style.paddingLeft = '0';
        }
      });
    };

    centerIfNeeded();
    window.addEventListener('resize', centerIfNeeded);
    return () => window.removeEventListener('resize', centerIfNeeded);
  }, [featuredListings, isLoading]);

  // Update active dot on scroll
  useEffect(() => {
    if (!carouselRef.current || featuredListings.length <= 1) return;

    let scrollTimeout;
    const carousel = carouselRef.current;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (!carousel || !carousel.children[0]) return;

          const cardWidth = carousel.children[0].offsetWidth + 16;
          const scrollLeft = carousel.scrollLeft;
          const index = Math.round(scrollLeft / cardWidth);
          setActiveIndex(Math.max(0, Math.min(index, featuredListings.length - 1)));
        });
      }, 200);
    };

    carousel.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [featuredListings.length]);

  // Mouse drag handlers for desktop
  const handleMouseDown = (e) => {
    if (isTouchDevice || e.button !== 0) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - carouselRef.current.offsetLeft;
    scrollLeftRef.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.cursor = 'grabbing';
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.5;
    carouselRef.current.scrollLeft = scrollLeftRef.current - walk;

    // Mark as dragged if moved more than 3px
    if (Math.abs(walk) > 3) {
      hasDraggedRef.current = true;
    }
  };

  const handleMouseUp = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      if (carouselRef.current) {
        carouselRef.current.style.cursor = 'grab';
      }
      // Reset hasDragged after a short delay to prevent click navigation
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 50);
    }
  };

  const handleMouseLeave = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      if (carouselRef.current) {
        carouselRef.current.style.cursor = 'grab';
      }
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 50);
    }
  };

  // Handle card click
  const handleCardClick = (listing, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't navigate if user was dragging
    if (hasDraggedRef.current) return;

    const title = listing.attributes?.title || '';
    const slug = createSlug(title);
    const listingId = listing.id?.uuid || listing.id;
    const listingPath = `/l/${slug}/${listingId}`;
    history.push(listingPath);
  };

  // Touch handlers for mobile
  const handleTouchStart = () => {
    touchStartTimeRef.current = Date.now();
    touchMovedRef.current = false;
    hasDraggedRef.current = false;
  };

  const handleTouchMove = () => {
    touchMovedRef.current = true;
    hasDraggedRef.current = true;
  };

  const handleTouchEnd = () => {
    // If user swiped, prevent navigation for a short time
    if (touchMovedRef.current && Date.now() - touchStartTimeRef.current > 150) {
      // Keep hasDraggedRef true to prevent click navigation
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 100);
    } else {
      // Quick tap - allow navigation
      hasDraggedRef.current = false;
    }
  };

  // Handle dot click
  const handleDotClick = (index) => {
    if (!carouselRef.current || !carouselRef.current.children[0]) return;
    const cardWidth = carouselRef.current.children[0].offsetWidth + 16;
    carouselRef.current.scrollTo({
      left: index * cardWidth,
      behavior: 'smooth'
    });
    setActiveIndex(index);
  };

  // Don't render anything while loading
  if (isLoading) {
    return null;
  }

  // Don't render if no listings
  if (featuredListings.length === 0) {
    return null;
  }

  // Render featured listings
  return (
    <div className={css.featuredContainer}>
      <div className={css.carouselWrapper}>
        <div
          ref={carouselRef}
          className={css.carousel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: isTouchDevice ? 'default' : 'grab' }}
        >
          {featuredListings.map((listing, index) => {
            const { title = '', price } = listing.attributes || {};

            // Get first image
            let firstImage = null;
            if (listing.images && listing.images.length > 0) {
              firstImage = listing.images[0];
            }

            // Get image URL
            let imageUrl = null;
            if (firstImage?.attributes?.variants) {
              imageUrl = firstImage.attributes.variants['listing-card']?.url ||
                        firstImage.attributes.variants['listing-card-2x']?.url ||
                        firstImage.attributes.variants['scaled-small']?.url ||
                        firstImage.attributes.variants['scaled-medium']?.url ||
                        Object.values(firstImage.attributes.variants)[0]?.url;
            }

            // Get author name
            let authorName = 'Unknown Seller';
            if (listing.author) {
              const author = ensureUser(listing.author);
              authorName = author?.attributes?.profile?.displayName || 'Unknown Seller';
            }

            // Format price
            let priceDisplay = 'Make an offer';
            if (price?.amount && price?.currency) {
              try {
                const amount = price.amount / 100;
                priceDisplay = new Intl.NumberFormat('en-AU', {
                  style: 'currency',
                  currency: price.currency
                }).format(amount);
              } catch (e) {
                console.warn('Failed to format price:', e);
              }
            }

            return (
              <div
                key={listing.id?.uuid || listing.id || index}
                className={css.featuredCard}
                onClick={(e) => handleCardClick(listing, e)}
              >
                <div className={css.imageContainer}>
                  <div className={css.featuredBadge}>Featured</div>
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={title}
                      className={css.cardImage}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const container = e.target.parentElement;
                        const fallback = container.querySelector(`.${css.imageFallback}`);
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  )}
                  <div className={css.imageFallback} style={{ display: imageUrl ? 'none' : 'flex' }}>
                    🏌️
                  </div>
                </div>
                <div className={css.cardContent}>
                  <div className={css.cardPrice}>{priceDisplay}</div>
                  <h3 className={css.cardTitle}>{title}</h3>
                  <div className={css.cardAuthor}>by {authorName}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation dots for mobile */}
        {featuredListings.length > 1 && (
          <div className={css.dotsContainer}>
            {featuredListings.map((_, index) => (
              <div
                key={index}
                className={`${css.dot} ${index === activeIndex ? css.activeDot : ''}`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFeatured;
